import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import FileUpload from "../../components/FileUpload/FileUpload";
import { getJaDisplayName } from "components/userUtil.ts";
import CompanySearchModal from "components/houjinApi/CompanySearchModal";
import CompanySearchByNumberModal from "components/houjinApi/CompanySearchByNumberModal";
import type { CompanyInfoByInvoice } from "components/invoiceApi/companyInfoApi";
import InvoiceSearchModal from "components/invoiceApi/InvoiceSearchModal";
import type { CompanyInfo } from "components/houjinApi/companyInfoApi";
import PostalCodeSearchModal from "components/zipCloudApi/PostalCodeSearchModal";
import type { AddressInfoByPostalCode } from "components/zipCloudApi/postalCodeApi";
import AnimationWrapper from "components/AnimationWrapper";
import PrivacyPolicyInfo from "components/PrivacyPolicyInfo.tsx";
import useCanShow from "./canShowField";
import IsShowField from "components/IsShowField";
import { useDetailRegister } from "./DetailRegisterProvider";

export default function DetailRegister() {
  const navigate = useNavigate();

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Use context instead of local state
  const {
    userDetail,
    setUserDetail,
    uploadedFiles,
    setUploadedFiles,
  } = useDetailRegister();

  // Destructure userDetail for easier access
  const {
    applicantName,
    applicantNameKana,
    birthDate,
    age,
    homePostalCode,
    homePrefecture,
    homeCity,
    homeAddress1,
    homeAddress2,
    homePhoneNumber,
    mobileNumber,
    companyName,
    invoiceNumber,
    corporateNumber,
    postalCode,
    prefecture,
    city,
    address1,
    address2,
    phoneNumber,
    faxNumber,
    industryType,
    annualSales,
    employeeCount,
    taxDelayType,
    taxDelayBalance,
    taxDelayStatus,
    familyStructure,
    factoringHistory,
    pastProviders,
    pastProviderAmount,
    currentProviderHistory,
    currentProviders,
    currentProviderAmount,
    personalInfoConsent,
  } = userDetail;

  // Helper functions to update specific fields in userDetail
  const updateField = (field: string, value: string) => {
    setUserDetail(prev => ({ ...prev, [field]: value }));
  };
  const updateNumberField = (field: string, value: string) => {
    const onlyDigits = value.replace(/\D+/g, "");
    const num = onlyDigits ? Number(onlyDigits) : 0;
    setUserDetail(prev => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
  };
  const updateBooleanField = (field: string, value: boolean) => {
    setUserDetail(prev => ({ ...prev, [field]: value }));
  };

  const canShow = useCanShow(
    applicantName,
    applicantNameKana,
    birthDate,
    age,
    homePostalCode,
    homePrefecture,
    homeCity,
    homeAddress1,
    homeAddress2,
    homePhoneNumber,
    mobileNumber,
    companyName,
    invoiceNumber,
    corporateNumber,
    postalCode,
    prefecture,
    city,
    address1,
    address2,
    phoneNumber,
    faxNumber,
    industryType,
    annualSales,
    employeeCount,
    taxDelayType,
    taxDelayBalance,
    taxDelayStatus,
    familyStructure,
    factoringHistory,
    pastProviders,
    pastProviderAmount,
    currentProviderHistory,
    currentProviders,
    currentProviderAmount,
    personalInfoConsent,
    uploadedFiles,
  );

  // State for company search modal
  const [isCompanySearchModalOpen, setIsCompanySearchModalOpen] = useState(false);

  // State for company search by number modal
  const [isCompanySearchByNumberModalOpen, setIsCompanySearchByNumberModalOpen] = useState(false);

  // State for postal code search modal
  const [isPostalCodeSearchModalOpen, setIsPostalCodeSearchModalOpen] = useState(false);

  // State for home postal code search modal
  const [isHomePostalCodeSearchModalOpen, setIsHomePostalCodeSearchModalOpen] = useState(false);

  // State for invoice search modal
  const [isInvoiceSearchModalOpen, setIsInvoiceSearchModalOpen] = useState(false);

  // Handle company selection from modal
  const handleCompanySelect = (company: CompanyInfo) => {
    setUserDetail(prev => ({
      ...prev,
      companyName: company.clientCompanyName,
      corporateNumber: company.corporateNumber,
      postalCode: company.postalCode,
      prefecture: company.prefecture,
      city: company.city,
      address1: company.address1,
      address2: "",
    }));
  };

  // Handle company info from invoice search
  const handleInvoiceCompanyInfo = (company: CompanyInfoByInvoice) => {
    setUserDetail(prev => ({
      ...prev,
      companyName: company.clientCompanyName,
      prefecture: company.prefecture,
      address1: company.address1,
      // Clear other fields as specified in the requirements
      postalCode: "",
      city: "",
      address2: "",
      corporateNumber: "",
    }));
  };

  // Handle address selection from postal code search modal
  const handleAddressSelect = (address: AddressInfoByPostalCode) => {
    setUserDetail(prev => ({
      ...prev,
      postalCode: address.postalCode,
      prefecture: address.prefecture,
      city: address.city,
      address1: address.address1,
    }));
  };

  // Handle home address selection from home postal code search modal
  const handleHomeAddressSelect = (address: AddressInfoByPostalCode) => {
    setUserDetail(prev => ({
      ...prev,
      homePostalCode: address.postalCode,
      homePrefecture: address.prefecture,
      homeCity: address.city,
      homeAddress1: address.address1,
    }));
  };

  const hasAnyValue = useMemo(() => {
    return applicantName || applicantNameKana || birthDate || age;
  }, [applicantName, applicantNameKana, birthDate, age]);

  const handleBack = () => {
    if (hasAnyValue) {
      if (!confirm("変更内容が失われますが、ページを離れてもよろしいですか？\n")) {
        return;
      }
    }
    navigate('/mypage');
  }

  // Form submission handler
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSubmitAttempted(true);

    if (!userDetail.personalInfoConsent) {
      alert("個人情報の取り扱いへの同意が必要です");
      return;
    }
    if (!validateForm()) {
      return;
    }

    navigate('/mypage/detail/register/confirm');
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // homePhoneNumber: 0文字又は10～12文字
    if (homePhoneNumber && (homePhoneNumber.length < 10 || homePhoneNumber.length > 12)) {
      newErrors.homePhoneNumber = '10～12文字で入力してください';
    }

    // mobileNumber: 11文字～13文字
    if (mobileNumber && (mobileNumber.length < 11 || mobileNumber.length > 13)) {
      newErrors.mobileNumber = '11～13文字で入力してください';
    }

    // phoneNumber: 10文字～13文字
    if (phoneNumber && (phoneNumber.length < 10 || phoneNumber.length > 13)) {
      newErrors.phoneNumber = '10～13文字で入力してください';
    }

    // faxNumber: 0文字又は10～12文字
    if (faxNumber && (faxNumber.length < 10 || faxNumber.length > 12)) {
      newErrors.faxNumber = '10～12文字で入力してください';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getMainErrorMessage = () => {
    return "入力内容にエラーがあります。上記をご確認ください。";
  };

  const hasValidationErrors = () => {
    return Object.keys(errors).length > 0;
  };

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 8,
        mb: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        会員情報登録（詳細）
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        詳細情報をご入力ください
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ mt: 1, width: "100%" }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="applicantName"
              label={getJaDisplayName("applicantName")}
              name="applicantName"
              autoComplete="name"
              value={applicantName}
              onChange={(e) => updateField("applicantName", e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder="山田　太郎"
              helperText="姓と名の間にスペースを入れてください（例：山田　太郎）"
            />
          </Grid>
          <IsShowField canShow={canShow('applicantNameKana')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="applicantNameKana"
                  label={getJaDisplayName("applicantNameKana")}
                  name="applicantNameKana"
                  value={applicantNameKana}
                  onChange={(e) => updateField("applicantNameKana", e.target.value)}
                  placeholder="ヤマダ　タロウ"
                  helperText="全角カタカナで入力してください"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('birthDate')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="birthDate"
                  label={getJaDisplayName("birthDate")}
                  name="birthDate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => updateField("birthDate", e.target.value)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      min: "1900-01-01",
                      max: "9999-12-31"
                    }
                  }}
                  helperText="西暦・月・日を入力してください"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('age')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="age"
                  label={getJaDisplayName("age")}
                  name="age"
                  type="number"
                  value={age.toString()}
                  onChange={(e) => updateNumberField("age", e.target.value)}
                  placeholder="35"
                  helperText="半角数字で入力してください"
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      min: 0,
                      max: 120
                    }
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('homePostalCode')}>
            <Grid size={12}>
              <AnimationWrapper>
                <Typography>
                  自宅情報
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ※ご自宅の情報をご入力ください
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="homePostalCode"
                  label={getJaDisplayName("homePostalCode")}
                  name="homePostalCode"
                  value={homePostalCode}
                  onChange={(e) => updateField("homePostalCode", e.target.value)}
                  slotProps={{
                    htmlInput: {
                      pattern: "^(\\d{3}-\\d{4}|\\d{7})$",
                    },
                    inputLabel: { shrink: true },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsHomePostalCodeSearchModalOpen(true)}
                          >
                            郵便番号検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                  placeholder="123-4567"
                  helperText={<>
                    例: 123-4567 または 1234567<br />
                    自動入力したい場合は検索ボタンから<br />
                    ※この検索により以下の項目が入力されます<br />
                    {getJaDisplayName("homePostalCode")}、
                    {getJaDisplayName("homePrefecture")}、
                    {getJaDisplayName("homeCity")}、
                    {getJaDisplayName("homeAddress1")}
                  </>}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('homePrefecture')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="homePrefecture"
                  label={getJaDisplayName("homePrefecture")}
                  name="homePrefecture"
                  value={homePrefecture}
                  onChange={(e) => updateField("homePrefecture", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="東京都"
                  helperText="都道府県名を入力してください（例：東京都）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('homeCity')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="homeCity"
                  label={getJaDisplayName("homeCity")}
                  name="homeCity"
                  value={homeCity}
                  onChange={(e) => updateField("homeCity", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="千代田区"
                  helperText="市区町村名を入力してください（例：千代田区）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('homeAddress1')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="homeAddress1"
                  label={getJaDisplayName("homeAddress1")}
                  name="homeAddress1"
                  value={homeAddress1}
                  onChange={(e) => updateField("homeAddress1", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="丸の内2-2-2"
                  helperText="番地・丁目などを入力してください（例：丸の内2-2-2）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('homeAddress2')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  fullWidth
                  id="homeAddress2"
                  label={getJaDisplayName("homeAddress2")}
                  name="homeAddress2"
                  value={homeAddress2}
                  onChange={(e) => updateField("homeAddress2", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="○○マンション101号室"
                  helperText="建物名・部屋番号など（任意）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('homePhoneNumber')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  fullWidth
                  id="homePhoneNumber"
                  label={getJaDisplayName("homePhoneNumber")}
                  name="homePhoneNumber"
                  value={homePhoneNumber}
                  onChange={(e) => updateField("homePhoneNumber", e.target.value)}
                  error={!!errors.homePhoneNumber}
                  slotProps={{
                    htmlInput: {
                      pattern: "[0-9\\-]+",
                    },
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  placeholder="03-0000-0000"
                  helperText={errors.homePhoneNumber || "数字とハイフンのみ入力可能"}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('mobileNumber')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="mobileNumber"
                  label={getJaDisplayName("mobileNumber")}
                  name="mobileNumber"
                  value={mobileNumber}
                  onChange={(e) => updateField("mobileNumber", e.target.value)}
                  error={!!errors.mobileNumber}
                  slotProps={{
                    htmlInput: {
                      pattern: "[0-9\\-]+",
                    },
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  placeholder="090-1234-5678"
                  helperText={errors.mobileNumber || "数字とハイフンのみ入力可能"}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('companyName')}>
            <Grid size={12}>
              <AnimationWrapper>
                <Typography>
                  法人情報
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  法人検索・インボイス検索・法人番号検索のいずれかを選択してください
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="companyName"
                  label={getJaDisplayName("companyName")}
                  name="companyName"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(e) => updateField("companyName", e.target.value)}
                  slotProps={{
                    inputLabel: {
                      shrink: true
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsCompanySearchModalOpen(true)}
                          >
                            法人名検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                  placeholder="サンプル株式会社"
                  helperText={<>正式名称で入力してください（例：サンプル株式会社）<br />自動入力したい場合は検索ボタンから</>}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('invoiceNumber')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  fullWidth
                  id="invoiceNumber"
                  label={getJaDisplayName("invoiceNumber")}
                  name="invoiceNumber"
                  value={invoiceNumber}
                  onChange={(e) => updateField("invoiceNumber", e.target.value)}
                  placeholder="T1234567890123"
                  slotProps={{
                    htmlInput: {
                      pattern: "^T\\d{13}$",
                    },
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsInvoiceSearchModalOpen(true)}
                          >
                            インボイス番号検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                  helperText={<>Tから始まる13桁の数字 (例: T1234567890123)<br />自動入力したい場合は検索ボタンから</>}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('corporateNumber')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  fullWidth
                  id="corporateNumber"
                  label={getJaDisplayName("corporateNumber")}
                  name="corporateNumber"
                  value={corporateNumber}
                  onChange={(e) => updateField("corporateNumber", e.target.value)}
                  placeholder="1234567890123"
                  slotProps={{
                    htmlInput: {
                      pattern: "^\\d{13}$",
                    },
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsCompanySearchByNumberModalOpen(true)}
                          >
                            法人番号検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                  helperText={<>13桁の数字<br />自動入力したい場合は検索ボタンから</>}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('postalCode')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="postalCode"
                  label={getJaDisplayName("postalCode")}
                  name="postalCode"
                  value={postalCode}
                  onChange={(e) => updateField("postalCode", e.target.value)}
                  placeholder="123-4567"
                  slotProps={{
                    htmlInput: {
                      pattern: "^(\\d{3}-\\d{4}|\\d{7})$",
                    },
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsPostalCodeSearchModalOpen(true)}
                          >
                            郵便番号検索
                          </Button>
                        </InputAdornment>
                      )
                    }
                  }}
                  helperText={<>
                    例: 123-4567 または 1234567<br />
                    自動入力したい場合は検索ボタンから<br />
                    ※この検索により以下の項目が入力されます<br />
                    {getJaDisplayName("postalCode")}、
                    {getJaDisplayName("prefecture")}、
                    {getJaDisplayName("city")}、
                    {getJaDisplayName("address1")}
                  </>}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('prefecture')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="prefecture"
                  label={getJaDisplayName("prefecture")}
                  name="prefecture"
                  value={prefecture}
                  onChange={(e) => updateField("prefecture", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="東京都"
                  helperText="都道府県名を入力してください（例：東京都）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('city')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="city"
                  label={getJaDisplayName("city")}
                  name="city"
                  value={city}
                  onChange={(e) => updateField("city", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="千代田区"
                  helperText="市区町村名を入力してください（例：千代田区）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('address1')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="address1"
                  label={getJaDisplayName("address1")}
                  name="address1"
                  value={address1}
                  onChange={(e) => updateField("address1", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="丸の内1-1-1"
                  helperText="番地・丁目などを入力してください（例：丸の内1-1-1）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('address2')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  fullWidth
                  id="address2"
                  label={getJaDisplayName("address2")}
                  name="address2"
                  value={address2}
                  onChange={(e) => updateField("address2", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="サンプルビル10階"
                  helperText="建物名・部屋番号など（任意）"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('phoneNumber')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="phoneNumber"
                  label={getJaDisplayName("phoneNumber")}
                  name="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => updateField("phoneNumber", e.target.value)}
                  error={!!errors.phoneNumber}
                  slotProps={{
                    htmlInput: {
                      pattern: "[0-9\\-]+",
                    },
                    inputLabel: {
                      shrink: true,
                    }
                  }}
                  placeholder="03-1234-5678"
                  helperText={errors.phoneNumber || "数字とハイフンのみ入力可能"}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('faxNumber')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  fullWidth
                  id="faxNumber"
                  label={getJaDisplayName("faxNumber")}
                  name="faxNumber"
                  value={faxNumber}
                  onChange={(e) => updateField("faxNumber", e.target.value)}
                  error={!!errors.faxNumber}
                  slotProps={{
                    htmlInput: {
                      pattern: "[0-9\\-]+",
                    },
                    inputLabel: {
                      shrink: true,
                    }
                  }}
                  placeholder="03-8765-4321"
                  helperText={errors.faxNumber || "数字とハイフンのみ入力可能"}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('industryType')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="industryType"
                  label={getJaDisplayName("industryType")}
                  name="industryType"
                  value={industryType}
                  onChange={(e) => updateField("industryType", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="建設業"
                  helperText="例：建設業、情報通信業など"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('annualSales')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="annualSales"
                  label={getJaDisplayName("annualSales")}
                  name="annualSales"
                  value={annualSales}
                  onChange={(e) => updateField("annualSales", e.target.value)}
                  slotProps={{
                    input: {
                      endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>円</Typography>,
                    },
                  }}
                  placeholder="1億"
                  helperText="例：1億円、5,000万円 など"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('employeeCount')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="employeeCount"
                  label={getJaDisplayName("employeeCount")}
                  name="employeeCount"
                  value={employeeCount}
                  onChange={(e) => updateField("employeeCount", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="正社員10人・パート5人"
                  helperText="例：正社員〇人・パート〇人"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
          <IsShowField canShow={canShow('familyStructure')}>
            <AnimationWrapper>
              <Box sx={{ mt: 2, mb: 0 }}>
                <Typography variant="subtitle1">
                  家族構成
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ※いない場合は本人の情報をご記入ください
                </Typography>
              </Box>
            </AnimationWrapper>
            <Grid size={12}>
              <AnimationWrapper>
                {Array.isArray(familyStructure) && familyStructure.map((member, idx) => (
                  <Box key={idx} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          required
                          label="続柄"
                          value={member.relationship}
                          onChange={(e) => {
                            const next = [...familyStructure];
                            next[idx] = { ...next[idx], relationship: e.target.value };
                            setUserDetail(prev => ({ ...prev, familyStructure: next }));
                          }}
                          slotProps={{ inputLabel: { shrink: true } }}
                          placeholder="配偶者"
                          helperText="例：配偶者、子、父 など"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          required
                          label="氏名"
                          value={member.fullName}
                          onChange={(e) => {
                            const next = [...familyStructure];
                            next[idx] = { ...next[idx], fullName: e.target.value };
                            setUserDetail(prev => ({ ...prev, familyStructure: next }));
                          }}
                          slotProps={{ inputLabel: { shrink: true } }}
                          placeholder="山田　花子"
                          helperText="姓と名の間にスペース（例：山田　花子）"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          required
                          type="number"
                          label="年齢"
                          value={member.age.toString()}
                          onChange={(e) => {
                            const num = e.target.value === '' ? 0 : Number(e.target.value);
                            const next = [...familyStructure];
                            next[idx] = { ...next[idx], age: isNaN(num) ? 0 : num };
                            setUserDetail(prev => ({ ...prev, familyStructure: next }));
                          }}
                          slotProps={{
                            inputLabel: { shrink: true },
                            htmlInput: {
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                              min: 0,
                              max: 120
                            }
                          }}
                          placeholder="40"
                          helperText="半角数字で入力してください"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          required
                          label="連絡先"
                          value={member.contactInfo}
                          onChange={(e) => {
                            const next = [...familyStructure];
                            next[idx] = { ...next[idx], contactInfo: e.target.value };
                            setUserDetail(prev => ({ ...prev, familyStructure: next }));
                          }}
                          slotProps={{ inputLabel: { shrink: true } }}
                          placeholder="090-1234-5678"
                          helperText="電話番号、メールアドレス等"
                        />
                      </Grid>
                      <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          color="error"
                          variant="text"
                          onClick={() => {
                            const next = Array.isArray(familyStructure) ? [...familyStructure] : [];
                            next.splice(idx, 1);
                            setUserDetail(prev => ({ ...prev, familyStructure: next }));
                          }}
                        >
                          削除
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                {(!Array.isArray(familyStructure) || familyStructure.length < 3) && (
                  <>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        ※複数名いる場合は追加ボタンをクリックしてください
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const next = Array.isArray(familyStructure) ? [...familyStructure] : [];
                          if (next.length >= 3) return;
                          next.push({ relationship: '', fullName: '', age: 0, contactInfo: '' });
                          setUserDetail(prev => ({ ...prev, familyStructure: next }));
                        }}
                      >
                        ＋ 追加
                      </Button>
                    </Box>
                  </>
                )}
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('factoringHistory')}>
            <Grid size={12}>
              <AnimationWrapper>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="factoringHistory-label">
                    {getJaDisplayName("factoringHistory")}
                  </InputLabel>
                  <Select
                    labelId="factoringHistory-label"
                    id="factoringHistory"
                    value={factoringHistory}
                    label={getJaDisplayName("factoringHistory")}
                    onChange={(e) => updateField("factoringHistory", e.target.value)}
                  >
                    <MenuItem value="あり">あり</MenuItem>
                    <MenuItem value="なし">なし</MenuItem>
                  </Select>
                </FormControl>
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('pastProviders')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required={factoringHistory === "あり"}
                  fullWidth
                  id="pastProviders"
                  label={getJaDisplayName("pastProviders")}
                  name="pastProviders"
                  value={pastProviders}
                  onChange={(e) => updateField("pastProviders", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="サンプル株式会社"
                  helperText="例：〇〇ファクタリング、△△株式会社"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('pastProviderAmount')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required={factoringHistory === "あり"}
                  fullWidth
                  id="pastProviderAmount"
                  label={getJaDisplayName("pastProviderAmount")}
                  name="pastProviderAmount"
                  value={pastProviderAmount.toString()}
                  onChange={(e) => updateNumberField("pastProviderAmount", e.target.value)}
                  helperText="半角数字で入力してください（万円）"
                  slotProps={{
                    input: {
                      endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>万円</Typography>,
                    },
                    htmlInput: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      min: 0,
                      max: 9999999999
                    },
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('currentProviderHistory')}>
            <Grid size={12}>
              <AnimationWrapper>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="currentProviderHistory-label">
                    {getJaDisplayName("currentProviderHistory")}
                  </InputLabel>
                  <Select
                    labelId="currentProviderHistory-label"
                    id="currentProviderHistory"
                    value={currentProviderHistory}
                    label={getJaDisplayName("currentProviderHistory")}
                    onChange={(e) => updateField("currentProviderHistory", e.target.value)}
                  >
                    <MenuItem value="あり">あり</MenuItem>
                    <MenuItem value="なし">なし</MenuItem>
                  </Select>
                </FormControl>
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('currentProviders')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required={currentProviderHistory === "あり"}
                  fullWidth
                  id="currentProviders"
                  label={getJaDisplayName("currentProviders")}
                  name="currentProviders"
                  value={currentProviders}
                  onChange={(e) => updateField("currentProviders", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="サンプル株式会社"
                  helperText="例：現在利用中の会社名を記入"
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('currentProviderAmount')}>
            <Grid size={12}>
              <AnimationWrapper>
                <TextField
                  margin="normal"
                  required={currentProviderHistory === "あり"}
                  fullWidth
                  id="currentProviderAmount"
                  label={getJaDisplayName("currentProviderAmount")}
                  name="currentProviderAmount"
                  value={currentProviderAmount.toString()}
                  onChange={(e) => updateNumberField("currentProviderAmount", e.target.value)}
                  helperText="半角数字で入力してください（万円）"
                  slotProps={{
                    input: {
                      endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>万円</Typography>,
                    },
                    htmlInput: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      min: 0,
                      max: 9999999999
                    },
                  }}
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>

          <IsShowField canShow={canShow('fileUpload')}>
            <Grid size={12}>
              <AnimationWrapper>
                <FileUpload
                  onFilesChange={setUploadedFiles}
                  files={uploadedFiles}
                  title={
                  <>
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                      画像ファイルのアップロード <span className="MuiFormLabel-asterisk">*</span>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                      顔写真付き証明書表・裏（必須）別途、保険証表（ない場合は不要）
                    </Typography>
                  </>
                  }
                />
              </AnimationWrapper>
            </Grid>
          </IsShowField>
        </Grid>

        <IsShowField canShow={canShow('personalInfoConsent')}>
          <Grid size={12}>
            <AnimationWrapper>
              <FormControl fullWidth margin="normal" sx={{ mt: 5 }}>
                <PrivacyPolicyInfo />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="personalInfoConsent"
                      checked={personalInfoConsent}
                      onChange={(e) => updateBooleanField("personalInfoConsent", e.target.checked)}
                      color="primary"
                      required
                    />
                  }
                  label={getJaDisplayName("personalInfoConsent")}
                />
              </FormControl>
            </AnimationWrapper>
          </Grid>
        </IsShowField>

        <IsShowField canShow={canShow('submitButton')}>
          <AnimationWrapper>
            {submitAttempted && hasValidationErrors() && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mb: 1, textAlign: "right" }}
              >
                {getMainErrorMessage()}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              color={submitAttempted && hasValidationErrors() ? "error" : "primary"}
            >
              確認する
            </Button>
          </AnimationWrapper>
        </IsShowField>
        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 3, mb: 2 }}
          onClick={handleBack}
        >
          戻る
        </Button>
      </Box>

      {process.env.NODE_ENV === "development" && (
        <Button
          variant="outlined"
          color="secondary"
          sx={{ mb: 2 }}
          onClick={() => {
            setUserDetail({
              applicantName: "山田太郎",
              applicantNameKana: "ヤマダタロウ",
              birthDate: "1990-01-01",
              age: 35,
              homePostalCode: "100-0001",
              homePrefecture: "東京都",
              homeCity: "千代田区",
              homeAddress1: "丸の内2-2-2",
              homeAddress2: "マンション101",
              homePhoneNumber: "03-0000-0000",
              phoneNumber: "03-1234-5678",
              faxNumber: "03-8765-4321",
              mobileNumber: "090-1234-5678",
              companyName: "サンプル株式会社",
              invoiceNumber: "T1234567890123",
              corporateNumber: "1234567890123",
              postalCode: "123-4567",
              prefecture: "東京都",
              city: "千代田区",
              address1: "丸の内1-1-1",
              address2: "サンプルビル10階",
              industryType: "情報通信業",
              annualSales: "1億円",
              employeeCount: "50",
              taxDelayType: "住民税",
              taxDelayBalance: 0,
              taxDelayStatus: "なし",
              familyStructure: [
                {
                  relationship: "ペット",
                  age: 198,
                  fullName: "ポチ",
                  contactInfo: "犬小屋",
                }
              ],
              factoringHistory: "あり",
              pastProviders: "サンプルファクタリング会社",
              pastProviderAmount: 500000,
              currentProviderHistory: "あり",
              currentProviders: "現在のサンプル会社",
              currentProviderAmount: 300000,
              personalInfoConsent: true,
            });
          }}
        >
          テストデータを入力
        </Button>
      )}

      {/* Company Search Modal */}
      <CompanySearchModal
        open={isCompanySearchModalOpen}
        onClose={() => setIsCompanySearchModalOpen(false)}
        onSelect={handleCompanySelect}
        initialSearchTerm={companyName}
        subTitle={<Typography variant="caption">
          ※法人種別を省いた名称を入力して検索してください。例：株式会社サンプルを検索したい場合、サンプルで検索<br />
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("companyName")}、
          {getJaDisplayName("corporateNumber")}、
          {getJaDisplayName("postalCode")}、
          {getJaDisplayName("prefecture")}、
          {getJaDisplayName("city")}、
          {getJaDisplayName("address1")}
        </Typography>}
      />

      {/* Company Search By Number Modal */}
      <CompanySearchByNumberModal
        open={isCompanySearchByNumberModalOpen}
        onClose={() => setIsCompanySearchByNumberModalOpen(false)}
        onSelect={handleCompanySelect}
        initialSearchTerm={corporateNumber}
        subTitle={<Typography variant="caption">
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("companyName")}、
          {getJaDisplayName("corporateNumber")}、
          {getJaDisplayName("postalCode")}、
          {getJaDisplayName("prefecture")}、
          {getJaDisplayName("city")}、
          {getJaDisplayName("address1")}
        </Typography>}
      />

      {/* Postal Code Search Modal */}
      <PostalCodeSearchModal
        open={isPostalCodeSearchModalOpen}
        onClose={() => setIsPostalCodeSearchModalOpen(false)}
        onSelect={handleAddressSelect}
        initialSearchTerm={postalCode}
        subTitle={<Typography variant="caption">
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("postalCode")}、
          {getJaDisplayName("prefecture")}、
          {getJaDisplayName("city")}、
          {getJaDisplayName("address1")}
        </Typography>}
      />

      {/* Invoice Search Modal */}
      <InvoiceSearchModal
        open={isInvoiceSearchModalOpen}
        onClose={() => setIsInvoiceSearchModalOpen(false)}
        onSelect={handleInvoiceCompanyInfo}
        initialSearchTerm={invoiceNumber}
        subTitle={<Typography variant="caption">
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("companyName")}、
          {getJaDisplayName("prefecture")}、
          {getJaDisplayName("address1")}
        </Typography>}
      />

      {/* Home Postal Code Search Modal */}
      <PostalCodeSearchModal
        open={isHomePostalCodeSearchModalOpen}
        onClose={() => setIsHomePostalCodeSearchModalOpen(false)}
        onSelect={handleHomeAddressSelect}
        initialSearchTerm={homePostalCode}
        subTitle={<Typography variant="caption">
          ※この検索により以下の項目が入力されます<br />
          {getJaDisplayName("homePostalCode")}、
          {getJaDisplayName("homePrefecture")}、
          {getJaDisplayName("homeCity")}、
          {getJaDisplayName("homeAddress1")}
        </Typography>}
      />
    </Container>
  );
}
